import {
  createFrontendPlugin,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import { HomePageLayoutBlueprint } from '@backstage/plugin-home-react/alpha';

import { rootRouteRef } from './routes';
import { searchWidget } from './home/widgets/SearchWidget';
import { quickLinksWidget } from './home/widgets/QuickLinksWidget';
import { myEntitiesWidget } from './home/widgets/MyEntitiesWidget';
import { governanceHealthWidget } from './home/widgets/GovernanceHealthWidget';
import { systemsMapWidget } from './home/widgets/SystemsMapWidget';

/** Página global de decisiones (ADR) con búsqueda sobre los ADR indexados. */
const decisionesPage = PageBlueprint.make({
  params: {
    path: '/decisiones',
    title: 'Decisiones (ADR)',
    routeRef: rootRouteRef,
    loader: () =>
      import('./pages/DecisionsPage').then(m => <m.DecisionsPage />),
  },
});

/** Card de gobernanza: solo aparece si la entidad tiene alguna anotación wib/*. */
const techDebtCard = EntityCardBlueprint.make({
  name: 'tech-debt',
  params: {
    type: 'info',
    filter: {
      $any: [
        { 'metadata.annotations.wib/tech-debt': { $exists: true } },
        { 'metadata.annotations.wib/criticality': { $exists: true } },
        { 'metadata.annotations.wib/spof-impact': { $exists: true } },
        { 'metadata.annotations.wib/migration-status': { $exists: true } },
        { 'metadata.annotations.wib/deployment-mode': { $exists: true } },
      ],
    },
    loader: () => import('./cards/TechDebtCard').then(m => <m.TechDebtCard />),
  },
});

/** Repositorio, CI y enlaces de despliegue. Solo componentes. */
const deploymentLinksCard = EntityCardBlueprint.make({
  name: 'deployment-links',
  params: {
    type: 'info',
    filter: { kind: 'component' },
    loader: () =>
      import('./cards/DeploymentLinksCard').then(m => (
        <m.DeploymentLinksCard />
      )),
  },
});

/** Consumidores de una librería (entidades con dependencyOf). */
const consumersCard = EntityCardBlueprint.make({
  name: 'consumers',
  params: {
    type: 'info',
    filter: { kind: 'component', 'spec.type': 'library' },
    loader: () =>
      import('./cards/RelatedEntitiesCard').then(m => <m.ConsumersCard />),
  },
});

/** Dependientes de un recurso (entidades con dependencyOf). */
const dependentsCard = EntityCardBlueprint.make({
  name: 'dependents',
  params: {
    type: 'info',
    filter: { kind: 'resource' },
    loader: () =>
      import('./cards/RelatedEntitiesCard').then(m => <m.DependentsCard />),
  },
});

/** Scorecard de Tech Insights agregado por componentes de sistema o dominio. */
const systemScorecardCard = EntityCardBlueprint.make({
  name: 'system-scorecard',
  params: {
    type: 'info',
    filter: { kind: { $in: ['system', 'domain'] } },
    loader: () =>
      import('./cards/SystemScorecardCard').then(m => (
        <m.SystemScorecardCard />
      )),
  },
});

/** Estado y enlaces del despliegue en Dokploy. */
const dokployCard = EntityCardBlueprint.make({
  name: 'dokploy',
  params: {
    type: 'info',
    filter: {
      'metadata.annotations.dokploy.com/application-id': { $exists: true },
    },
    loader: () => import('./cards/DokployCard').then(m => <m.DokployCard />),
  },
});

/** CI en Bitbucket Pipelines: últimos pipelines del repositorio vía proxy. */
const pipelinesCard = EntityCardBlueprint.make({
  name: 'pipelines',
  params: {
    type: 'info',
    filter: {
      'metadata.annotations.bitbucket.org/project-slug': { $exists: true },
    },
    loader: () =>
      import('./cards/PipelinesCard').then(m => <m.PipelinesCard />),
  },
});

/** Pestaña Dependencias con el grafo de relaciones de la entidad. */
const dependenciesContent = EntityContentBlueprint.make({
  name: 'dependencies',
  params: {
    path: '/dependencias',
    title: 'Dependencias',
    group: 'architecture',
    filter: {
      kind: { $in: ['component', 'api', 'resource', 'system', 'domain'] },
    },
    loader: () =>
      import('./content/DependenciesContent').then(m => (
        <m.DependenciesContent />
      )),
  },
});

/** Layout del home del portal de arquitectura. */
const homeLayout = HomePageLayoutBlueprint.make({
  params: {
    loader: () => import('./home/HomeLayout').then(m => m.HomeLayout),
  },
});

export const governancePlugin = createFrontendPlugin({
  pluginId: 'governance',
  extensions: [
    decisionesPage,
    techDebtCard,
    deploymentLinksCard,
    consumersCard,
    dependentsCard,
    systemScorecardCard,
    dokployCard,
    pipelinesCard,
    dependenciesContent,
    homeLayout,
    searchWidget,
    quickLinksWidget,
    myEntitiesWidget,
    governanceHealthWidget,
    systemsMapWidget,
  ],
  routes: {
    root: rootRouteRef,
  },
});
